import React, {useEffect, useState, useMemo} from 'react';
import Wrapper from '../../components/Wrapper';
import DashboardHeader from '../../components/DashboardHeader';
import {useTranslation} from '../../hooks/useTranslation';
import './index.scss';
import TinyBarChart from '../../components/Charts/TinyBarChart';
import CustomPieChart from '../../components/Charts/CustomPieChart';
import TotalPieChart from '../../components/Charts/TotalPieChart';
import VerticalPieChart from '../../components/Charts/VerticalPieChart';
import DashboardTable from '../../components/Dashboard/DashboardTable';
import MetricsTable from '../../components/Dashboard/MetricsTable';
import TinyStackedBarChart from '../../components/Charts/TinyStackedBarChart';
import {useNavigate, useParams} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import {
  useLazyDashboardPersonalStatsQuery,
  useLazyDashboardEquipmentStatsQuery,
  useLazyDashboardPersonalInfoQuery,
  useLazyDashboardOutStandingInspectionsQuery,
  useLazyDashboardOutStandingRtsQuery,
  useLazyDashboardCorrectiveActionsQuery,
  useLazyDashboardOpenStatsQuery,
  useLazyDashboardAveragePerInspectionQuery,
  useGetUsersRolesQuery,
  useLazyDashboardInspectionsQuery,
  useLazyGetAgencyByIdQuery,
  useSetAgencyToUserMutation,
  useLazyMetricsInspectionsMissedQuery,
  useLazyMetricsCorrectiveActionsStatsQuery,
} from '../../store/builder/inspections.api';
import {tableHead2MockData} from '../AdvancedDashboard/mockData';
import {setLoader} from '../../store/builder/reducer';
import moment from 'moment';
import AverageTimeBar from '../../components/Charts/AverageTimeBar';
import UpcomingInspectionsTable from '../../components/Metrics/UpcomingInspectionsTable';
import OpenCorrectiveActionsTable from '../../components/Metrics/OpenCorrectiveActionsTable';
import {Helmet} from 'react-helmet';
import {toast} from 'react-toastify';
import {setAgenciesList} from '../../store/builder/agenciesListSlice';
import {useSearchParams} from 'react-router-dom';

const Dashboard = () => {
  const {t} = useTranslation();
  const [searchParams] = useSearchParams();
  const agency_id = searchParams.get('agency');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [outStandingInsp, setOutStandingInsp] = useState(null);
  const [tableOptions, setTableOptions] = useState([
    {value: '', label: t('dashboard.all')},
  ]);
  const [inspectionBarFilter, setInspectionFilter] = useState();
  const [completedInspectionsFilter, setCompletedInspectionsFilter] = useState([
    {value: 'week', label: t('dashboard.lastWeek')},
    {value: 'month', label: t('dashboard.lastMonth')},
    {value: 'quarter', label: t('dashboard.lastQuarter')},
    {value: 'semiannual', label: t('dashboard.last6Months')},
    {value: 'year', label: t('dashboard.ttm')},
  ]);
  const [filterPeriodOptions, setFilterPeriodOptions] = useState([
    {value: 'week', label: t('dashboard.lastWeek')},
    {value: 'month', label: t('dashboard.lastMonth')},
    {value: 'quarter', label: t('dashboard.lastQuarter')},
    {value: 'semiannual', label: t('dashboard.last6Months')},
    {value: 'annual', label: t('dashboard.ttm')},
  ]);
  const filterLastPeriodShortOptions = useMemo(
    () => [
      {value: 'weekly', label: t('advancedMetrics.lastWeek')},
      {value: 'monthly', label: t('advancedMetrics.lastMonth')},
      {value: 'quarterly', label: t('advancedMetrics.lastQuarter')},
      {value: 'semiannual', label: t('advancedMetrics.last6Months')},
      {value: 'annual', label: t('advancedMetrics.ttm')},
      {value: 'mtd', label: t('inspections.statusOptions.monthToDate')},
    ],
    [t],
  );
  const [chosenMissedFilters, setChosenMissedFilters] = useState({
    period: null,
    filter_for_period: 'weekly',
  });
  const currentFbo = +agency_id || localStorage.getItem('current-fbo');
  const [getMetricsMissed, {data: metricsMissedData}] =
    useLazyMetricsInspectionsMissedQuery();
  const [getAgencyById, {data: agencyData}] = useLazyGetAgencyByIdQuery();
  const [averageFilters, setAverageFilters] = useState({
    filter_period: filterPeriodOptions[0].value,
    facility_id: null,
    equipment_type_id: null,
    employee_id: null,
  });
  const [
    getDashboardPersonalStats,
    {
      data: dashboardPersonalStatsData,
      error: dashboardPersonalStatsError,
      isFetching: dashboardPersonalStatsIsLoading,
      isSuccess: dashboardPersonalStatsIsSuccess,
      isError: dashboardPersonalStatsIsError,
    },
  ] = useLazyDashboardPersonalStatsQuery();

  const [
    getDashboardEquipmentStats,
    {
      data: dashboardEquipmentStatsData,
      error: dashboardEquipmentStatsError,
      isFetching: dashboardEquipmentStatsIsLoading,
      isSuccess: dashboardEquipmentStatsIsSuccess,
      isError: dashboardEquipmentStatsIsError,
    },
  ] = useLazyDashboardEquipmentStatsQuery();

  const [
    getDashboardPersonalInfo,
    {
      data: dashboardPersonalInfoData,
      error: dashboardPersonalInfoError,
      isFetching: dashboardPersonalInfoIsLoading,
      isSuccess: dashboardPersonalInfoIsSuccess,
      isError: dashboardPersonalInfoIsError,
    },
  ] = useLazyDashboardPersonalInfoQuery();

  const [
    getDashboardOutStandingInspections,
    {
      data: dashboardOutStandingInspectionsData,
      error: dashboardOutStandingInspectionsError,
      isFetching: dashboardOutStandingInspectionsIsLoading,
      isSuccess: dashboardOutStandingInspectionsIsSuccess,
      isError: dashboardOutStandingInspectionsIsError,
    },
  ] = useLazyDashboardOutStandingInspectionsQuery();

  const [
    getDashboardOutStandingRts,
    {
      data: dashboardOutStandingRtsData,
      error: dashboardOutStandingRtsError,
      isFetching: dashboardOutStandingRtsIsLoading,
      isSuccess: dashboardOutStandingRtsIsSuccess,
      isError: dashboardOutStandingRtsIsError,
    },
  ] = useLazyDashboardOutStandingRtsQuery();

  const [
    getDashboardCorrectiveActions,
    {
      data: dashboardCorrectiveActionsData,
      error: dashboardCorrectiveActionsError,
      isFetching: dashboardCorrectiveActionsIsLoading,
      isSuccess: dashboardCorrectiveActionsIsSuccess,
      isError: dashboardCorrectiveActionsIsError,
    },
  ] = useLazyDashboardCorrectiveActionsQuery();

  const [
    getDashboardInspections,
    {
      data: dashboardInspectionsData,
      error: dashboardInspectionsError,
      isFetching: dashboardInspectionsIsLoading,
      isSuccess: dashboardInspectionsIsSuccess,
      isError: dashboardInspectionsIsError,
    },
  ] = useLazyDashboardInspectionsQuery();

  const [getMetricsCorrectiveStats, {data: correctiveStatsData}] =
    useLazyMetricsCorrectiveActionsStatsQuery();

  const [getDashboardOpenStats, {data: openStatsData}] =
    useLazyDashboardOpenStatsQuery();

  const {
    data: userRolesData,
    isSuccess: userRolesIsSuccess,
    isLoading: userRolesIsLoading,
    isError: userRolesIsError,
    error: userRolesError,
  } = useGetUsersRolesQuery();

  useEffect(() => {
    dispatch(setLoader(true));

    Promise.all([
      getDashboardPersonalStats(currentFbo ? {agency: currentFbo} : {}),
      getDashboardEquipmentStats(currentFbo ? {agency: currentFbo} : {}),
      getDashboardPersonalInfo(
        currentFbo ? {name: '', agency: currentFbo} : {name: ''},
      ),
      getDashboardOutStandingInspections(
        currentFbo ? {agency: currentFbo} : {},
      ),
      getDashboardOutStandingRts(currentFbo ? {agency: currentFbo} : {}),
      getDashboardCorrectiveActions(currentFbo ? {agency: currentFbo} : {}),
      getDashboardOpenStats(currentFbo ? {agency: currentFbo} : {}),
      getDashboardInspections(
        currentFbo ? {period: 'week', agency: currentFbo} : {period: 'week'},
      ),
    ]).finally(() => {
      dispatch(setLoader(false));
    });
  }, []);

  useEffect(() => {
    if (userRolesIsSuccess) {
      if (tableOptions?.length < 2) {
        setTableOptions(old => [
          ...old,
          ...userRolesData?.map(item => ({
            value: item?.key,
            label: item?.name,
          })),
        ]);
      }
    }
  }, [userRolesIsSuccess]);

  // Update filter period options when language changes
  useEffect(() => {
    setFilterPeriodOptions([
      {value: 'weekly', label: t('dashboard.missedInspections.lastWeek')},
      {value: 'monthly', label: t('dashboard.missaedInspections.lastMonth')},
      {value: 'quarterly', label: t('dashboard.missaedInspections.lastQuarter')},
      {value: 'semiannual', label: t('dashboard.missaedInspections.last6Months')},
      {value: 'annual', label: t('dashboard.missaedInspections.ttm')},
    ]);
  }, [t]);

  // Update table options when language changes
  useEffect(() => {
    setTableOptions([{value: '', label: t('dashboard.all')}]);
  }, [t]);

  useEffect(() => {
    setCompletedInspectionsFilter([
      {value: 'week', label: t('dashboard.lastWeek')},
      {value: 'month', label: t('dashboard.lastMonth')},
      {value: 'quarter', label: t('dashboard.lastQuarter')},
      {value: 'semiannual', label: t('dashboard.last6Months')},
      {value: 'annual', label: t('dashboard.ttm')},
    ]);
  }, [t]);

  useEffect(() => {
    if (currentFbo) {
      getMetricsCorrectiveStats({agency: currentFbo});
      getDashboardOpenStats({agency: currentFbo});
      getDashboardEquipmentStats({agency: currentFbo});
      getDashboardPersonalStats({agency: currentFbo});
      getDashboardPersonalInfo({name: '', agency: currentFbo});
      getDashboardOutStandingInspections({agency: currentFbo});
      getDashboardOutStandingRts({agency: currentFbo});
      getDashboardCorrectiveActions({agency: currentFbo});
      getDashboardInspections({period: 'week', agency: currentFbo});
    } else {
      getDashboardOpenStats({});
      getDashboardEquipmentStats({});
      getDashboardPersonalStats({});
      getDashboardPersonalInfo({name: ''});
      getDashboardOutStandingInspections({});
      getDashboardOutStandingRts({});
      getDashboardCorrectiveActions({});
      getDashboardInspections({period: 'week'});
    }
  }, [currentFbo]);

  useEffect(() => {
    if (currentFbo) {
      getMetricsMissed({
        agency: currentFbo,
        period: chosenMissedFilters?.period,
        filter_for_period: chosenMissedFilters?.filter_for_period,
      });
    }
  }, [currentFbo, chosenMissedFilters]);

  useEffect(() => {
    if (currentFbo) {
      getAgencyById(currentFbo);
    }
  }, [currentFbo]);

  function filterNullValues(obj) {
    const newObj = {};
    for (const key in obj) {
      if (obj[key]) {
        newObj[key] = obj[key];
      }
    }
    return newObj;
  }

  useEffect(() => {
    let obj = {
      filter_period: averageFilters?.filter_period,
      facility_id: averageFilters?.facility_id,
      equipment_type_id: averageFilters?.equipment_type_id,
      employee_id: averageFilters?.employee_id,
    };

    const newObj = filterNullValues(obj);

    let params = new URLSearchParams(newObj).toString();
  }, [averageFilters]);

  const handleTableChange = e => {
    getDashboardPersonalInfo(
      currentFbo ? {name: e.value, agency: currentFbo} : {name: e.value},
    );
  };

  const handleInspectionFilter = e => {
    getDashboardInspections(
      currentFbo ? {period: e, agency: currentFbo} : {period: e},
    );
  };

  const handleFilterMissed = e => {
    setChosenMissedFilters(old => ({...old, period: e.value}));
  };
  const handleFilterMissedSecond = e => {
    setChosenMissedFilters(old => ({...old, filter_for_period: e.value}));
  };
  const navigateToInspection = id => {
    navigate(`/inspections/${id}`);
  };

  const openCorrectiveCount = openStatsData?.open_ca ?? 0;
  const returnToServiceCount = openStatsData?.open_rts ?? 0;

  return (
    <Wrapper>
      <Helmet>
        <title>
          {agency_id && agencyData?.data?.full_title
            ? `${agencyData.data.full_title} - ${t('dashboard.pageTitle')}`
            : t('dashboard.pageTitle')}
        </title>
      </Helmet>
      <DashboardHeader
        title={
          agency_id && agencyData?.data?.full_title
            ? `${agencyData.data.full_title} - ${t('dashboard.title')}`
            : t('dashboard.title')
        }
        type={'dashboard'}
        action={() => navigate(`/advanced-metrics?agency=${currentFbo}`)}
      />
      <div className='dashboard'>
        <div className='dashboard__block'>
          <div className='dashboard__block-numbers'>
            <TotalPieChart data={dashboardEquipmentStatsData} />
          </div>
          <div className='dashboard__block-numbers'>
            <div className='dashboard__number-section'>
              <span className='dashboard__number-label'>
                {t('dashboard.openCorrectiveActionsCurrent')}
              </span>
              <span className='dashboard__number-value'>
                {openCorrectiveCount}
              </span>
            </div>
            <div className='dashboard__number-section'>
              <span className='dashboard__number-label'>
                {t('dashboard.returnToServiceInspectionsCurrent')}
              </span>
              <span className='dashboard__number-value'>
                {returnToServiceCount}
              </span>
            </div>
          </div>
        </div>
        <div className='dashboard__block'>
          <UpcomingInspectionsTable agency={currentFbo} />
        </div>
        <div className='dashboard__block'>
          <OpenCorrectiveActionsTable agency={currentFbo} />
        </div>
        <div className='dashboard__block'>
          <div className='metrics__block'>
            <div className='metrics__block-left'>
              <VerticalPieChart
                level={'lower'}
                customColors={['#DA6767', '#E9EAEB']}
                data={metricsMissedData?.metric}
                title={t('advancedMetrics.missedInspections')}
                width={'pre-mid'}
              />
            </div>
            <div className='metrics__block-right h-370'>
              <MetricsTable
                headers={tableHead2MockData}
                body={metricsMissedData?.inspections}
                cols={'2fr 3fr 1.5fr 2fr 1fr'}
                title={t('advancedMetrics.missedInspections')}
                selectTitle={t('advancedMetrics.period')}
                showSelect={true}
                showSecondSelect={true}
                secondOptions={filterLastPeriodShortOptions}
                secondSelectDefault={filterLastPeriodShortOptions[0]}
                width={'shrinked'}
                tableHeight={'metrics-small'}
                rowHeight={'metrics-small'}
                options={filterPeriodOptions}
                handleSelect={handleFilterMissed}
                handleSecondSelect={handleFilterMissedSecond}
                rowPointer={true}
                rowAction={navigateToInspection}
              />
            </div>
          </div>
        </div>
        <div className='dashboard__block'>
          <TinyStackedBarChart
            options={completedInspectionsFilter}
            title={t('dashboard.completedInspections')}
            data={dashboardInspectionsData?.data?.map(item => ({
              ...item,
              name: item?.key,
              value1: item?.value?.nfc,
              value2: Number(item?.value?.total) - Number(item?.value?.nfc) - Number(item?.value?.partial),
              value3: item?.value?.partial,
            }))}
            handleChange={handleInspectionFilter}
          />
          <TinyBarChart
            size={'big'}
            data={dashboardOutStandingInspectionsData?.data?.map(item => ({
              ...item,
              key: moment(item.key).format('MM/DD/YY'),
            }))}
            title={t('dashboard.openInspections')}
            isTime={false}
          />
        </div>
        <div className='dashboard__block'>
          <TinyBarChart
            size={'big'}
            data={dashboardCorrectiveActionsData?.data?.map(item => ({
              ...item,
              key: moment(item.key).format('MM/DD/YY'),
            }))}
            title={t('dashboard.correctiveActionsCreated')}
            isTime={false}
          />
          <TinyBarChart
            size={'big'}
            data={dashboardOutStandingRtsData?.data?.map(item => ({
              ...item,
              key: moment(item.key).format('MM/DD/YY'),
            }))}
            title={t('dashboard.openReturnToServiceInspections')}
            isTime={false}
          />
        </div>
        <div className='dashboard__block'>
          <VerticalPieChart
            legendHeight={'centered'}
            surface={true}
            width={'small'}
            data={dashboardPersonalStatsData?.data}
            title={t('dashboard.personnelStats')}
          />
          <DashboardTable
            title={t('dashboard.userInformation')}
            showSelect={true}
            data={dashboardPersonalInfoData?.data}
            options={tableOptions}
            handleChange={handleTableChange}
          />
        </div>
      </div>
    </Wrapper>
  );
};

export default Dashboard;
